import * as Sentry from "@sentry/react-router";
import type { ReactNode } from "react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
	isRouteErrorResponse,
	useMatches,
	useNavigate,
	useRouteError,
	useRouteLoaderData,
} from "react-router";
import { useErrorBoundaryTracking } from "~/hooks/use-error-boundary-tracking.hook";
import { isRouteErrorResponse as isCustomRouteErrorResponse } from "~/tooling/error";
import {
	buildRouteErrorFingerprint,
	getDeepestRouteId,
} from "~/tooling/route-error-fingerprint";
import { isNetworkError } from "~/utils/network-error-reporter";
import { isVersionMismatchError } from "~/utils/version-mismatch-handler";
import { ErrorPage } from "./error-page.component";

const PAYMENT_STATUS_ROUTE_IDS = new Set([
	"routes/checkout_.payment.pay",
	"routes/checkout_.payment.redirect-from",
	"routes/checkout_.payment.result",
]);

const getErrorDebugInfo = (error: unknown) => {
	if (isRouteErrorResponse(error)) {
		let errorDataStringified = "";

		try {
			errorDataStringified = JSON.stringify(error.data);
		} catch {}

		return (
			<div>
				<h1>
					{error.status} {error.statusText}
				</h1>
				<p>{errorDataStringified}</p>
			</div>
		);
	}
	if (error instanceof Error) {
		return (
			<div>
				<h1>Error</h1>
				<p>{error.message}</p>
				<p>The stack trace is:</p>
				<pre>{error.stack}</pre>
			</div>
		);
	}
	return <h1>Unknown Error</h1>;
};

const isStaleRouteManifestError = (error: unknown): boolean => {
	if (!(error instanceof Error)) return false;
	return error.message.startsWith('No result found for routeId "routes/');
};

const isStaleClientBundleError = (error: unknown): boolean => {
	if (!(error instanceof Error)) return false;
	return isStaleRouteManifestError(error) || isVersionMismatchError(error);
};

function RequestHeadersTooLargeErrorPage({
	isPaymentStatusRoute,
	message,
	debugInfo,
	errorId,
}: Readonly<{
	isPaymentStatusRoute: boolean;
	message: string;
	debugInfo?: ReactNode;
	errorId?: string;
}>) {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const destination = isPaymentStatusRoute ? "/profile" : "/";

	return (
		<ErrorPage
			title={
				isPaymentStatusRoute
					? t("errors.paymentStatusUnavailable.title")
					: t("errors.requestHeadersTooLarge.title")
			}
			message={message}
			primaryButton={{
				label: isPaymentStatusRoute
					? t("errors.paymentStatusUnavailable.action")
					: t("errors.requestHeadersTooLarge.action"),
				action: () => navigate(destination, { replace: true }),
			}}
			debugInfo={debugInfo}
			errorId={errorId}
		/>
	);
}

function isClientAbortedRouteError(error: unknown): boolean {
	return isRouteErrorResponse(error) && error.status === 499;
}

export function RootErrorBoundary() {
	const { ENV } = useRouteLoaderData("root") ?? {};
	const error = useRouteError();
	const routeId = getDeepestRouteId(useMatches());
	const [errorId, setErrorId] = useState<string>();
	const isNetworkFailure = error instanceof Error && isNetworkError(error);
	const isServiceUnavailable =
		isRouteErrorResponse(error) && error.status >= 500 && error.status < 600;
	const isClientAbortedRequest = isClientAbortedRouteError(error);
	const isRequestHeaderFieldsTooLarge =
		isRouteErrorResponse(error) && error.status === 431;
	const isPaymentStatusRoute = PAYMENT_STATUS_ROUTE_IDS.has(routeId);
	const isStaleBundle = isStaleClientBundleError(error);

	// Force a full page reload when users have a stale client bundle after deployment.
	// This happens when the client's route manifest doesn't match the server's routes.
	// Fixes JUPITER-WEB-5T4 and similar stale bundle errors.
	// Uses sessionStorage to prevent infinite reload loops if the error persists.
	useEffect(() => {
		if (!isStaleBundle || typeof window === "undefined") return;
		const key = "stale-route-manifest-reload";
		try {
			if (sessionStorage.getItem(key)) return;
			sessionStorage.setItem(key, "1");
		} catch {
			// Storage unavailable: skip auto-reload to avoid a loop.
			return;
		}
		window.location.reload();
	}, [isStaleBundle]);

	useEffect(() => {
		if (isNetworkFailure || isStaleBundle || isClientAbortedRequest) {
			return;
		}

		let capturedErrorId: string;

		if (isRouteErrorResponse(error)) {
			const errorMessage =
				`Route Error: ${error.status} ${error.statusText || ""}`.trim();
			const routeError = new Error(errorMessage);
			routeError.name = "RouteErrorResponse";

			capturedErrorId = Sentry.captureException(routeError, {
				fingerprint: buildRouteErrorFingerprint(error.status, routeId),
				tags: {
					errorType: "RouteErrorResponse",
					statusCode: error.status,
				},
				extra: {
					status: error.status,
					statusText: error.statusText,
				},
			});
		} else {
			capturedErrorId = Sentry.captureException(error);
		}

		setErrorId(capturedErrorId);
	}, [error, isClientAbortedRequest, isNetworkFailure, isStaleBundle, routeId]);

	const { t } = useTranslation();
	const requestHeadersErrorMessage = t(
		isPaymentStatusRoute
			? "errors.paymentStatusUnavailable.message"
			: "errors.requestHeadersTooLarge.message",
	);

	// Check for custom error message from throwRouteError utility (needed before tracking)
	const customErrorMessage =
		isRouteErrorResponse(error) && isCustomRouteErrorResponse(error.data)
			? error.data.error.message
			: undefined;

	useErrorBoundaryTracking({
		errorId,
		isNetworkFailure,
		isStaleBundle,
		isServiceUnavailable,
		customErrorMessage: isRequestHeaderFieldsTooLarge
			? requestHeadersErrorMessage
			: customErrorMessage,
	});

	const canShowRouteErrors = ENV?.SHOW_ROUTE_ERRORS;
	const debugInfo = canShowRouteErrors ? getErrorDebugInfo(error) : undefined;

	if (isClientAbortedRequest) {
		return null;
	}

	return (
		<RootErrorView
			error={error}
			errorId={errorId}
			debugInfo={debugInfo}
			isNetworkFailure={isNetworkFailure}
			isServiceUnavailable={isServiceUnavailable}
			isRequestHeaderFieldsTooLarge={isRequestHeaderFieldsTooLarge}
			isPaymentStatusRoute={isPaymentStatusRoute}
			customErrorMessage={customErrorMessage}
			requestHeadersErrorMessage={requestHeadersErrorMessage}
		/>
	);
}

function RootErrorView({
	error,
	errorId,
	debugInfo,
	isNetworkFailure,
	isServiceUnavailable,
	isRequestHeaderFieldsTooLarge,
	isPaymentStatusRoute,
	customErrorMessage,
	requestHeadersErrorMessage,
}: {
	error: unknown;
	errorId?: string;
	debugInfo?: ReactNode;
	isNetworkFailure: boolean;
	isServiceUnavailable: boolean;
	isRequestHeaderFieldsTooLarge: boolean;
	isPaymentStatusRoute: boolean;
	customErrorMessage?: string;
	requestHeadersErrorMessage: string;
}) {
	const navigate = useNavigate();
	const { t } = useTranslation();
	const reloadPage = () => {
		if (typeof window !== "undefined") {
			window.location.reload();
		}
	};

	if (isNetworkFailure) {
		return (
			<ErrorPage
				title={t("errors.network.title")}
				message={t("errors.network.message")}
				primaryButton={{
					label: t("errors.network.retry"),
					action: reloadPage,
				}}
				debugInfo={debugInfo}
			/>
		);
	}

	if (isServiceUnavailable) {
		return (
			<ErrorPage
				title={t("errors.serviceUnavailable.title")}
				message={customErrorMessage ?? t("errors.serviceUnavailable.message")}
				primaryButton={{
					label: t("errors.serviceUnavailable.retry"),
					action: reloadPage,
				}}
				debugInfo={debugInfo}
				errorId={errorId}
			/>
		);
	}

	if (isRequestHeaderFieldsTooLarge) {
		return (
			<RequestHeadersTooLargeErrorPage
				isPaymentStatusRoute={isPaymentStatusRoute}
				message={requestHeadersErrorMessage}
				debugInfo={debugInfo}
				errorId={errorId}
			/>
		);
	}

	if (isRouteErrorResponse(error) && customErrorMessage) {
		return (
			<ErrorPage
				title={t("errors.somethingWentWrong")}
				message={customErrorMessage}
				primaryButton={{
					label: t("errors.notFound.backToHomepage"),
					action: () => navigate("/"),
				}}
				debugInfo={debugInfo}
				errorId={errorId}
			/>
		);
	}

	return (
		<ErrorPage
			title={t("errors.notFound.title")}
			message={t("errors.notFound.message")}
			primaryButton={{
				label: t("errors.notFound.backToHomepage"),
				action: () => navigate("/"),
			}}
			debugInfo={debugInfo}
			errorId={errorId}
		/>
	);
}
